import { isEmpty, isNil } from 'lodash-es'
import { ProviderAllocation } from 'types/dispatchConfig'

export enum ProviderNames {
  AAA = 'AAA',
  URGENTLY = 'ULY',
  ALLSTATE = 'ALLSTATE',
}

export interface ProviderCount {
  provider: string
  count: number
}

//getNextProvider explanation:
//Get the largest provider ratio (factor).
//Divide the largest factor by given factor, so in the example of 1:3 we would multiply
//the first provider’s count by 3/1 or 3.
//So for example if the counts are 9:30 we would get 27:30 and dispatch to the first,
//but if the count is 9:26 we would get 27:26 which would dispatch to the second.
//This works for 1:2:3 or any other arbitrary factors.
//If the counts are 5:9:15 we would multiply the first number by 3/1 or 3 and get 15.
//The second by 3/2 so it would round up 13.5 up to 14.
//And the third by 3/3 or 1 which would give us a final set of counts of 15:14:15.
//We would dispatch to the second, thus maintaining the 1:2:3 ratio.

export const getNextProvider = (stateConfig, providerCounts) => {
  const providerCountsArray: ProviderCount[] = Object.entries(providerCounts).map(([key, value]) => ({
    provider: key,
    count: value as number,
  }))

  // if for some reason the default config doesn't load, use 1:1:1 as a failsafe
  if (isEmpty(stateConfig) || isNil(stateConfig)) {
    const sortedCountsArray = providerCountsArray.sort((a, b) => {
      const valA = a.count
      const valB = b.count
      return valA - valB
    })
    return sortedCountsArray[0].provider.toUpperCase()
  }

  //get largest ratio for multiplication
  const largestRatio = stateConfig.sort((a, b) => {
    const valA = a.ratio
    const valB = b.ratio
    return valB - valA
  })[0].ratio

  //multiply provider counts by largest ratio divided by the current provider's ratio
  let newCounts: ProviderCount[] = []
  for (let [key, value] of Object.entries(providerCounts)) {
    const providerAlloc: ProviderAllocation = stateConfig.find((entry) => entry.provider === key.toUpperCase())

    if (providerAlloc === undefined) {
      continue
    } //skip providers not in our given state config

    // divide the largest ratio by our current ratio to get our balancing factor
    const factor = largestRatio / providerAlloc.ratio

    // multiply our provider count by the balancing factor
    const newValue = (value as number) * factor
    newCounts.push({ provider: key, count: newValue })
  }

  //sort by lowest count
  const sortedCounts = newCounts.sort((a, b) => {
    const valA = a.count
    const valB = b.count
    return valA - valB
  })

  //return provider with lowest count
  return sortedCounts[0].provider.toUpperCase()
}

export const getSecondaryProvider = (stateConfig) => {
  for (const configItem of stateConfig) {
    if (configItem.provider !== ProviderNames.AAA) {
      return configItem.provider
    }
  }
  return ProviderNames.URGENTLY
}
